import { useImperativeHandle, useRef, useState } from "react";

import CustomColorPickerModal from "./CustomColorPickerModal";
import CustomGradientPickerModal from "./CustomGradientPickerModal";
import { areColorsEqual, decodeColorObject } from "./ColorPicker.utils";
import { ColorPickerControl } from "@theorgchartdev/storybook";
import { filterDuplicateColors } from "@/Utils/DataLib";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import { getLocalizedColorPickerStrings } from "@/Utils/Utils";

const readCustomColors = wsMgr => {
  const oi = wsMgr.GetOrgInfo();
  if (!oi) {
    return [];
  }

  const customColors = oi.GetCustomColors();
  const deDupedCustomColors = filterDuplicateColors(customColors, ["#FFFFFF"]);
  return deDupedCustomColors;
};

const CompositeColorPicker = props => {
  const ctx = useWSMgrContext();
  const wsMgr = ctx.wsMgr.state;

  const standardOpenCtrlRef = useRef(null);
  useImperativeHandle(props.openCtrlRef, () => standardOpenCtrlRef.current);

  const handleStandardColorSelect = color => {
    props.onChange(decodeColorObject(color));
  };

  const [isCustomColorModalOpen, setCustomColorModalOpen] = useState(false);
  const [isCustomGradientModalOpen, setCustomGradientModalOpen] = useState(false);

  const customColors = readCustomColors(wsMgr);

  const triggerTestId = props.dataTestId ? `${props.dataTestId}-btn` : props.triggerTestId;
  const popoverTestId = props.dataTestId ? `${props.dataTestId}-popover` : props.popoverTestId;

  const handleLaunchCustomColor = () => {
    standardOpenCtrlRef.current?.setOpen(false);
    setCustomColorModalOpen(true);
  };

  const handleCloseCustomColorModal = () => {
    setCustomColorModalOpen(false);
  };

  const handleLaunchCustomGradient = () => {
    standardOpenCtrlRef.current?.setOpen(false);
    setCustomGradientModalOpen(true);
  };

  const handleCloseCustomGradientModal = () => {
    setCustomGradientModalOpen(false);
  };

  const handleCustomColorSave = color => {
    handleCloseCustomColorModal();
    handleCloseCustomGradientModal();

    props.onChange(decodeColorObject(color));

    const includesColor = customColors.some(c => areColorsEqual(c, color));
    if (includesColor || customColors.length >= 20) {
      return;
    }

    wsMgr.UpdateTheme([{ customcolors: [...customColors, color] }]);
  };

  return (
    <>
      <ColorPickerControl
        openCtrlRef={standardOpenCtrlRef}
        onCustomAdd={handleLaunchCustomColor}
        onCustomAddGradient={handleLaunchCustomGradient}
        customColors={customColors}
        onChange={handleStandardColorSelect}
        currentColor={props.currentColor}
        showGradient={props.showGradient}
        showNoFill={props.showNoFill}
        trigger={props.trigger}
        triggerProps={{ "data-testid": triggerTestId }}
        popoverProps={{
          enabled: props.enabled,
          floatingProps: { "data-testid": popoverTestId }
        }}
        textTags={getLocalizedColorPickerStrings()}
      />
      <CustomColorPickerModal
        show={isCustomColorModalOpen}
        onClose={handleCloseCustomColorModal}
        onSave={handleCustomColorSave}
      />
      <CustomGradientPickerModal
        show={isCustomGradientModalOpen}
        onClose={handleCloseCustomGradientModal}
        onSave={handleCustomColorSave}
        customColors={customColors}
        onCustomAdd={handleLaunchCustomColor}
      />
    </>
  );
};

export default CompositeColorPicker;
